import React, {ReactNode} from "react";
import {useLocation} from "react-router-dom";
import {
  styled,
  DropdownSelect,
  DropdownSelectText,
  ThinScrollbar,
  FlexColumn,
  Toggle,
} from "@reside/ui";
import {UserPermissionEnum} from "../../../utils/types/user";

import {IDateRangeMomentGlobal} from "../../../store/reporting/reporting.common";

import {Layout, LayoutContent} from "../../../atoms/admin-layout";
import {ReportingPageActionRow} from "./ReportingPageActionRow";
import {
  CompareWrapper,
  CustomDateRangePreset,
  DateRangePresets,
} from "../../../atoms/reporting-compare";
import {ReportContextConsumer} from "../ReportContext";
import {UseChecklistItems} from "../reporting-menu/useChecklistItems";
import {ReportingMenu} from "../reporting-menu";
import {PDFSpinner} from "../PDFSpinner";
import {BlankSlate} from "../BlankSlate";
import {UseQuicksightCurrentUserDashboards} from "../hooks/useQuicksightCurrentUserDashboards";

import {RequiredUserPermissionsSome} from "../../../atoms/admin-user-permissions/RequiredUserPermissionsSome";
import {ScrollSpyProvider} from "../../../hooks/useScrollSpy";
import {AdminNavigationHeader} from "../../../atoms/admin-navigation-header";

import "./style.scss";
import {Copyright} from "../../../atoms/copyright";

type Props = {
  children: ReactNode;
  isActionRowVisible?: boolean;
};

export const ReportingLayout = ({
  children,
  isActionRowVisible = true,
}: Props) => {
  const location = useLocation();
  const skipBlankSlate = location.pathname.startsWith("/admin/permissions");

  return (
    <WideLayout>
      <AdminNavigationHeader />
      <WideLayoutContent>
        <ScrollSpyProvider>
          <ReportContextConsumer>
            {({
              activeFacilitiesIds,
              pageLoading,
              baseDateRange,
              setBaseDateRange,
            }) => {
              return (
                <FullHeightRow>
                  <UseQuicksightCurrentUserDashboards>
                    {({data: dashboardItems = []}) => (
                      <>
                        <RequiredUserPermissionsSome
                          permissions={[UserPermissionEnum.CHECKLIST_ITEMS]}
                          noPermissionChildren={
                            <ReportingMenu dashboardItems={dashboardItems} />
                          }
                        >
                          <UseChecklistItems>
                            {({
                              /**
                               * When the checklistItems is undefined, the query is in progress so we display dummy menu item "Loading..."
                               */
                              data: checklistItems,
                            }) => (
                              <ReportingMenu
                                dashboardItems={dashboardItems}
                                checklistItems={checklistItems}
                              />
                            )}
                          </UseChecklistItems>
                        </RequiredUserPermissionsSome>
                        <ReportsColumn>
                          {isActionRowVisible && (
                            <UseChecklistItems>
                              {({data: checklistItems}) => (
                                <ReportingPageActionRow
                                  checklistItems={checklistItems}
                                  dashboardItems={dashboardItems}
                                  dateSelector={
                                    <Toggle>
                                      {({
                                        isOpened,
                                        setIsOpened,
                                        outsideClickIgnoreClass,
                                      }) => (
                                        <div style={{position: "relative"}}>
                                          {isOpened && (
                                            <CompareWrapper
                                              global
                                              className={
                                                outsideClickIgnoreClass
                                              }
                                              chartId="global"
                                              appliedDateRange={{
                                                ...baseDateRange,
                                                from: baseDateRange.from,
                                                to: baseDateRange.to,
                                              }}
                                              appliedCompareDateRange={
                                                baseDateRange
                                              }
                                              onApply={dateRange => {
                                                setIsOpened(false);
                                                setBaseDateRange(dateRange);
                                              }}
                                            />
                                          )}
                                          <DropdownSelect<IDateRangeMomentGlobal>
                                            label="Date Range"
                                            trigger={({isOpened}) => (
                                              <DropdownSelectText
                                                isOpened={isOpened}
                                                label={baseDateRange.label}
                                              />
                                            )}
                                            selectItem={selectedPreset =>
                                              selectedPreset.id ===
                                              CustomDateRangePreset.id
                                                ? setIsOpened(true)
                                                : setBaseDateRange(
                                                    selectedPreset,
                                                  )
                                            }
                                            selectedItem={baseDateRange as any}
                                            items={[
                                              ...DateRangePresets.map(
                                                dateRange => ({
                                                  ...dateRange,
                                                  from: dateRange.from(),
                                                  to: dateRange.to(),
                                                }),
                                              ),
                                              {
                                                ...CustomDateRangePreset,
                                                from: baseDateRange.from,
                                                to: baseDateRange.to,
                                              },
                                            ]}
                                          />
                                        </div>
                                      )}
                                    </Toggle>
                                  }
                                />
                              )}
                            </UseChecklistItems>
                          )}
                          <Scrollable className="js-reporting-scroll-container">
                            <PDFSpinner isLoading={pageLoading} />
                            {skipBlankSlate ? (
                              children
                            ) : (
                              <BlankSlate facilities={activeFacilitiesIds}>
                                {children}
                              </BlankSlate>
                            )}
                          </Scrollable>
                        </ReportsColumn>
                      </>
                    )}
                  </UseQuicksightCurrentUserDashboards>
                </FullHeightRow>
              );
            }}
          </ReportContextConsumer>
        </ScrollSpyProvider>
      </WideLayoutContent>
      <Copyright />
    </WideLayout>
  );
};

const WideLayout = styled(Layout)`
  min-height: 100vh;
  max-height: 100vh;
`;

const WideLayoutContent = styled(LayoutContent)`
  flex-grow: 1;
  overflow: hidden;
  max-width: none;
  padding: 20px ${({theme}) => theme.space[7]}px;
`;

const FullHeightRow = styled(ThinScrollbar)`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const Scrollable = styled.div`
  flex-grow: 1;
  overflow-y: auto;
`;

const ReportsColumn = styled(FlexColumn)`
  overflow: hidden;
  flex: 2; // Must be greater than the menu column
`;
